<template>
  <div>
    <app-api-call
      class="mx-3"
      :call-result="callResult"
    />

    <v-row class="ma-0 pa-3 align-center">
      <v-col
        cols="12"
        md="4"
        class="pa-1"
      >
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          :label="$i18n.t('$l.administration.flowiiStatus')"
          dense
          outlined
          hide-details
          @change="loadData"
        />
      </v-col>
      <v-col
        cols="12"
        md="4"
        class="pa-1"
      >
        <v-select
          v-model="limit"
          :items="limitOptions"
          :label="$i18n.t('$l.administration.flowiiLimit')"
          dense
          outlined
          hide-details
          @change="loadData"
        />
      </v-col>
      <v-col
        cols="12"
        md="4"
        class="pa-1 d-flex justify-end"
      >
        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              :loading="loading"
              v-bind="attrs"
              v-on="on"
              @click="loadData"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>{{ $i18n.t('$l.app.reload') }}</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-row class="ma-0 px-3 pb-3">
      <v-col
        v-for="summary in statusSummary"
        :key="summary.status"
        cols="12"
        sm="6"
        md="2"
        class="pa-1"
      >
        <v-sheet
          outlined
          rounded
          class="pa-2"
        >
          <div class="text-caption grey--text text--darken-1">
            {{ summary.status }}
          </div>
          <div class="text-h6">
            {{ summary.count }}
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="jobs"
      :loading="loading"
      :mobile-breakpoint="0"
      :items-per-page="25"
      show-expand
      single-expand
      class="ma-0 pa-0"
      dense
    >
      <template v-slot:[`item.status`]="{ item }">
        <v-chip
          small
          :color="statusColor(item.status)"
          dark
        >
          {{ item.status }}
        </v-chip>
      </template>

      <template v-slot:[`item.created`]="{ item }">
        <span style="white-space: nowrap">{{ $utils.formatDate(item.created) }}</span>
      </template>

      <template v-slot:[`item.lastModified`]="{ item }">
        <span style="white-space: nowrap">{{ $utils.formatDate(item.lastModified) }}</span>
      </template>

      <template v-slot:[`item.nextAttemptAt`]="{ item }">
        <span style="white-space: nowrap">{{ $utils.formatDate(item.nextAttemptAt) }}</span>
      </template>

      <template v-slot:[`item.lastAttemptAt`]="{ item }">
        <span style="white-space: nowrap">{{ $utils.formatDate(item.lastAttemptAt) }}</span>
      </template>

      <template v-slot:[`item.lastError`]="{ item }">
        <span
          class="flowii-error"
          :title="item.lastError"
        >
          {{ item.lastError }}
        </span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="canRetry(item)"
              icon
              small
              color="primary"
              :loading="retryingJobId === item.id"
              v-bind="attrs"
              v-on="on"
              @click="retryJob(item)"
            >
              <v-icon small>
                mdi-refresh
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $i18n.t('$l.administration.flowiiRetry') }}</span>
        </v-tooltip>
      </template>

      <template v-slot:expanded-item="{ headers: expandedHeaders, item }">
        <td :colspan="expandedHeaders.length">
          <v-container
            fluid
            class="pa-3 flowii-job-detail"
          >
            <v-row dense>
              <v-col
                v-for="detail in createJobDetails(item)"
                :key="detail.label"
                cols="12"
                sm="6"
                md="3"
              >
                <div class="text-caption grey--text text--darken-1">
                  {{ detail.label }}
                </div>
                <div class="flowii-detail-value">
                  {{ detail.value || '-' }}
                </div>
              </v-col>
              <v-col cols="12">
                <div class="text-caption grey--text text--darken-1">
                  {{ $i18n.t('$l.administration.flowiiLastError') }}
                </div>
                <pre class="flowii-error-detail">{{ item.lastError || '-' }}</pre>
              </v-col>
            </v-row>
          </v-container>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
export default {
  name: 'FlowiiInvoiceJobs',
  data () {
    return {
      callResult: { finished: true, authorized: true, error: null, info: null },
      loading: false,
      retryingJobId: null,
      statusFilter: 'ALL',
      limit: 100,
      jobs: [],
      limitOptions: [50, 100, 200, 500],
      statusOptions: [
        { text: this.$i18n.t('$l.app.all'), value: 'ALL' },
        { text: 'NEW', value: 'NEW' },
        { text: 'PROCESSING', value: 'PROCESSING' },
        { text: 'RETRY', value: 'RETRY' },
        { text: 'FAILED', value: 'FAILED' },
        { text: 'EMAIL_SENT', value: 'EMAIL_SENT' }
      ],
      headers: [
        { text: 'ID', value: 'id' },
        { text: this.$i18n.t('$l.administration.flowiiStatus'), value: 'status' },
        { text: this.$i18n.t('$l.administration.flowiiAttempts'), value: 'attemptCount' },
        { text: this.$i18n.t('$l.administration.flowiiStripeInvoice'), value: 'stripeInvoiceId' },
        { text: this.$i18n.t('$l.administration.flowiiCustomerEmail'), value: 'customerEmail' },
        { text: this.$i18n.t('$l.administration.flowiiTotalAmount'), value: 'totalAmount' },
        { text: this.$i18n.t('$l.administration.flowiiInvoiceId'), value: 'flowiiInvoiceId' },
        { text: this.$i18n.t('$l.administration.flowiiInvoiceNumber'), value: 'flowiiInvoiceNumber' },
        { text: this.$i18n.t('$l.administration.flowiiPaymentId'), value: 'flowiiPaymentId' },
        { text: this.$i18n.t('$l.administration.flowiiNextAttempt'), value: 'nextAttemptAt' },
        { text: this.$i18n.t('$l.administration.flowiiLastAttempt'), value: 'lastAttemptAt' },
        { text: this.$i18n.t('$l.administration.flowiiLastError'), value: 'lastError' },
        { text: this.$i18n.t('$l.administration.created'), value: 'created' },
        { text: this.$i18n.t('$l.administration.flowiiLastModified'), value: 'lastModified' },
        { text: this.$i18n.t('$l.app.actions'), value: 'actions', align: 'end', sortable: false }
      ]
    }
  },
  computed: {
    statusSummary () {
      const counts = {}
      this.jobs.forEach(job => {
        counts[job.status] = (counts[job.status] || 0) + 1
      })
      return Object.keys(counts).sort().map(status => ({ status, count: counts[status] }))
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      this.callResult.error = null
      this.$xapi.get(`administration/flowiiInvoiceJobs?status=${this.statusFilter}&limit=${this.limit}`)
        .then(r => {
          this.jobs = r.data || []
        })
        .catch(e => {
          if (e.response && e.response.localizedMessage) {
            this.callResult.error = e.response.localizedMessage
          } else {
            this.callResult.error = 'Error'
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    retryJob (job) {
      this.retryingJobId = job.id
      this.callResult.error = null
      this.$xapi.post(`administration/flowiiInvoiceJobs/retry?id=${job.id}`)
        .then(() => {
          this.loadData()
        })
        .catch(e => {
          if (e.response && e.response.localizedMessage) {
            this.callResult.error = e.response.localizedMessage
          } else {
            this.callResult.error = 'Error'
          }
        })
        .finally(() => {
          this.retryingJobId = null
        })
    },
    canRetry (job) {
      return job.status === 'FAILED' || job.status === 'RETRY'
    },
    createJobDetails (job) {
      return [
        { label: 'ID', value: job.id },
        { label: this.$i18n.t('$l.administration.flowiiStatus'), value: job.status },
        { label: this.$i18n.t('$l.administration.flowiiAttempts'), value: job.attemptCount },
        { label: this.$i18n.t('$l.administration.flowiiStripeInvoice'), value: job.stripeInvoiceId },
        { label: this.$i18n.t('$l.administration.flowiiCustomerEmail'), value: job.customerEmail },
        { label: this.$i18n.t('$l.administration.flowiiTotalAmount'), value: job.totalAmount },
        { label: this.$i18n.t('$l.administration.flowiiCurrency'), value: job.currency },
        { label: this.$i18n.t('$l.administration.flowiiInvoiceId'), value: job.flowiiInvoiceId },
        { label: this.$i18n.t('$l.administration.flowiiInvoiceNumber'), value: job.flowiiInvoiceNumber },
        { label: this.$i18n.t('$l.administration.flowiiPaymentId'), value: job.flowiiPaymentId },
        { label: this.$i18n.t('$l.administration.flowiiNextAttempt'), value: this.$utils.formatDate(job.nextAttemptAt) },
        { label: this.$i18n.t('$l.administration.flowiiLastAttempt'), value: this.$utils.formatDate(job.lastAttemptAt) },
        { label: this.$i18n.t('$l.administration.created'), value: this.$utils.formatDate(job.created) },
        { label: this.$i18n.t('$l.administration.flowiiLastModified'), value: this.$utils.formatDate(job.lastModified) }
      ]
    },
    statusColor (status) {
      if (status === 'FAILED') {
        return 'error'
      }
      if (status === 'RETRY') {
        return 'warning'
      }
      if (status === 'PROCESSING') {
        return 'primary'
      }
      return 'success'
    }
  }
}
</script>

<style lang="scss" scoped>
.flowii-error {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flowii-job-detail {
  background: #fafafa;
}

.flowii-detail-value {
  overflow-wrap: anywhere;
  font-size: 0.875rem;
}

.flowii-error-detail {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: inherit;
  font-size: 0.875rem;
  margin: 0;
}
</style>
